import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, memo } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createPortal } from "react-dom";

const DATE_PICKER_THEME_STYLE_ID = "date-input-with-cal-global-styles";

/** Injected once — avoids dozens of duplicate <style> tags on heavy forms (major layout cost). */
const DATE_INPUT_CAL_STYLES = `
    .date-only-picker .react-datepicker__month-container {
      border-right: none !important;
    }
    .react-datepicker {
    justify-content: center;
      font-family: inherit;
      border: none;
      background-color: transparent;
    }
    .react-datepicker__header {
      background-color: transparent;
      border-bottom: 1px solid #e2e8f0;
      padding-top: 0;
    }
    .react-datepicker__current-month {
      font-weight: 600;
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
    }
    .react-datepicker__day-names {
      margin-bottom: 0;
    }
    .react-datepicker__day-name {
      color: #64748b;
      font-size: 0.75rem;
      width: 2rem;
      line-height: 2rem;
    }
    .react-datepicker__month {
      margin: 0;
    }
    .react-datepicker__day {
      width: 2rem;
      line-height: 2rem;
      border-radius: 9999px;
      margin: 0.1rem;
      font-size: 0.875rem;
      color: #1e293b;
    }
    .react-datepicker__day:hover {
      background-color: #f1f5f9;
      border-radius: 9999px;
    }
    .react-datepicker__day--selected {
      background-color: #3b82f6;
      color: white;
    }
    .react-datepicker__day--selected:hover {
      background-color: #2563eb;
    }
    .react-datepicker__day--today {
      font-weight: bold;
      color: #3b82f6;
    }
    .react-datepicker__day--outside-month {
      color: #cbd5e1;
    }
    .react-datepicker__navigation {
      top: 0.75rem;
    }
    .react-datepicker__navigation--previous {
      left: 0.5rem;
      border-right-color: #64748b;
    }
    .react-datepicker__navigation--next {
      right: 0.5rem;
      border-left-color: #64748b;
    }
    .dark .react-datepicker {
      background-color: #1e293b;
    }
    .dark .react-datepicker__header {
      background-color: #1e293b;
      border-bottom-color: #334155;
    }
    .dark .react-datepicker__current-month {
      color: #f1f5f9;
    }
    .dark .react-datepicker__day-name {
      color: #94a3b8;
    }
    .dark .react-datepicker__day {
      color: #e2e8f0;
    }
    .dark .react-datepicker__day:hover {
      background-color: #334155;
    }
    .dark .react-datepicker__day--selected {
      background-color: #3b82f6;
      color: white;
    }
    .dark .react-datepicker__day--today {
      color: #60a5fa;
    }
    .dark .react-datepicker__day--outside-month {
      color: #475569;
    }
`;

function ensureDatePickerThemeStyles() {
    if (typeof document === "undefined") return;
    if (document.getElementById(DATE_PICKER_THEME_STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = DATE_PICKER_THEME_STYLE_ID;
    el.textContent = DATE_INPUT_CAL_STYLES;
    document.head.appendChild(el);
}

const DATE_PARSE_FORMATS = [
    "DD MMM YYYY",
    "DD-MMM-YYYY",
    "DD/MMM/YYYY",
    "DD MMMM YYYY",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "MM-DD-YYYY",
    "YYYY-MM-DD",
    "DDMMYYYY",
    "MMDDYYYY",
];

function parseFlexibleDateInput(dateStr) {
    for (let i = 0; i < DATE_PARSE_FORMATS.length; i++) {
        const m = moment(dateStr, DATE_PARSE_FORMATS[i], true);
        if (m.isValid()) return m;
    }
    return moment(dateStr);
}

function normalizeDatePropForCompare(v) {
    if (v == null || v === "") return "";
    const m = moment(v);
    return m.isValid() ? m.format("YYYY-MM-DD") : String(v);
}

const CalendarCustomHeader = memo(function CalendarCustomHeader({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}) {
    return (
        <div className="flex justify-between items-center px-2 mb-2">
            <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                type="button"
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {moment(date).format("MMMM YYYY")}
            </div>
            <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                type="button"
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    );
});

const DateInputwithcal = ({
    label,
    name,
    value,
    onChange,
    placeholder = "DD MMM YYYY",
    onError,
    required = false,
    disabled = false
}) => {
    const [displayValue, setDisplayValue] = useState("");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [error, setError] = useState("");
    const isControlled = useRef(false);
    const inputRef = useRef(null);
    const datePickerRef = useRef(null);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const [positionReady, setPositionReady] = useState(false);

    useLayoutEffect(() => {
        ensureDatePickerThemeStyles();
    }, []);

    const updatePosition = useCallback(() => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const pickerWidth = 280;
    const pickerHeight = 320;

    let left = rect.left + scrollLeft + (rect.width / 2) - (pickerWidth / 2);
    let top = rect.bottom + scrollTop + 5;

    if (left + pickerWidth > window.innerWidth + scrollLeft) {
        left = window.innerWidth + scrollLeft - pickerWidth - 10;
    }
    if (left < scrollLeft + 10) {
        left = scrollLeft + 10;
    }
    if (rect.bottom + pickerHeight + scrollTop > window.innerHeight + scrollTop) {
        top = rect.top + scrollTop - pickerHeight - 5;
    }

    setPickerPosition({ top, left });
    setPositionReady(true); // 👈 mark as ready only after position is known
}, []);

    const lastExternalValueRef = useRef(null);

    useEffect(() => {
        const key = normalizeDatePropForCompare(value);
        if (lastExternalValueRef.current === key) return;
        lastExternalValueRef.current = key;

        if (isControlled.current) return;

        if (value) {
            const m = moment(value);
            if (m.isValid()) {
                const formatted = m.format("DD MMM YYYY");
                setDisplayValue(formatted);
                setSelectedDate(m.toDate());
            } else {
                if (typeof value === "string" && value.match(/^\d{2} [A-Za-z]{3} \d{4}$/)) {
                    setDisplayValue(value);
                    const parsed = parseFlexibleDateInput(value);
                    if (parsed.isValid()) setSelectedDate(parsed.toDate());
                } else {
                    setDisplayValue("");
                    setSelectedDate(null);
                }
            }
        } else {
            setDisplayValue("");
            setSelectedDate(null);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                datePickerRef.current &&
                !datePickerRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowDatePicker(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showDatePicker) {
            updatePosition();
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);

            return () => {
                window.removeEventListener("scroll", updatePosition, true);
                window.removeEventListener("resize", updatePosition);
            };
        }
    }, [showDatePicker, updatePosition]);

    const handleDatePickerChange = useCallback((date) => {
        if (!date) {
            setDisplayValue("");
            setSelectedDate(null);
            setError("");
            setShowDatePicker(false);
            onChange(name, null);
            return;
        }
        const m = moment(date);
        const formatted = m.format("DD MMM YYYY");
        setDisplayValue(formatted);
        setSelectedDate(date);
        setError("");
        setShowDatePicker(false);
        onChange(name, m.format("YYYY-MM-DD"));
    }, [name, onChange]);

    const handleInputChangeLocal = useCallback((e) => {
        const input = e.target.value;
        isControlled.current = true;
        setDisplayValue(input);
        requestAnimationFrame(() => {
            isControlled.current = false;
        });
    }, []);

    const handleBlur = useCallback(() => {
        if (displayValue.trim()) {
            const m = parseFlexibleDateInput(displayValue);
            if (m.isValid()) {
                const corrected = m.format("DD MMM YYYY");
                setDisplayValue(corrected);
                setSelectedDate(m.toDate());
                setError("");
                onError?.(name, false);
                onChange(name, m.format("YYYY-MM-DD"));
            } else {
                setError("Invalid date. Use format: DD MMM YYYY or DDMMYYYY (e.g., 04032026)");
                onError?.(name, true);
            }
        } else {
            setDisplayValue("");
            setSelectedDate(null);
            setError("");
            onError?.(name, false);
            onChange(name, "");
        }
        isControlled.current = false;
    }, [displayValue, name, onChange, onError]);

   const handleFocus = useCallback(() => {
    setPositionReady(false); // 👈 reset before opening
    setShowDatePicker(true);
    requestAnimationFrame(updatePosition);
}, [updatePosition]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") e.target.blur();
        if (e.key === "Escape") setShowDatePicker(false);
    }, []);

    const selectToday = useCallback(() => handleDatePickerChange(new Date()), [handleDatePickerChange]);

    const clearDate = useCallback(() => {
        setDisplayValue("");
        setSelectedDate(null);
        setError("");
        onError?.(name, false);
        onChange(name, "");
        setShowDatePicker(false);
    }, [name, onChange, onError]);

  const togglePicker = useCallback(() => {
    setShowDatePicker((open) => {
        if (open) setPositionReady(false); // 👈 reset on close
        return !open;
    });
    requestAnimationFrame(updatePosition);
}, [updatePosition]);

    const renderCustomHeader = useCallback(
        (headerProps) => <CalendarCustomHeader {...headerProps} />,
        []
    );

    return (
        <div className="w-full relative">
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    name={name}
                    value={displayValue}
                    onChange={handleInputChangeLocal}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    className={`w-full px-3 py-2 text-sm border rounded-lg pr-10
                            bg-white dark:bg-slate-800 
                            text-slate-900 dark:text-white 
                            focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]
                            ${error ? "border-red-500" : "border-slate-300 dark:border-slate-700"}`}
                />
                <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    onClick={togglePicker}
                    disabled={disabled}
                >
                    📅
                </button>
            </div>

            {showDatePicker && !disabled && positionReady && createPortal(
                <div
                    ref={datePickerRef}
                    style={{
                        position: "absolute",
                        top: pickerPosition.top,
                        left: pickerPosition.left,
                        zIndex: 99999
                    }}
                    className="date-only-picker"
                >
                    <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg p-2 min-w-[280px] justify-center">
                        <DatePicker
                            selected={selectedDate}
                            onChange={handleDatePickerChange}
                            inline
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            dateFormat="dd MMM yyyy"
                            className="border-0"
                            wrapperClassName="w-full"
                            calendarClassName="border-0 !bg-transparent"
                            renderCustomHeader={renderCustomHeader}
                        />
                        <div className="flex justify-between gap-2 pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={selectToday}
                                className="flex-1 px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={clearDate}
                                className="flex-1 px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    );
};

function propsAreEqual(prev, next) {
    return (
        prev.name === next.name &&
        normalizeDatePropForCompare(prev.value) === normalizeDatePropForCompare(next.value) &&
        prev.disabled === next.disabled &&
        prev.placeholder === next.placeholder &&
        prev.required === next.required &&
        prev.onChange === next.onChange &&
        prev.onError === next.onError
    );
}

export default memo(DateInputwithcal, propsAreEqual);
